import { findAuthoringRecommendationTask } from "./authoring-schedule-recommendations"

let clearSelectedRecommendationField = null
let clearRecommendationRowFlash = null

export function canFocusAuthoringRecommendationTarget({ recommendation, draft }) {
    switch (`${recommendation?.changeType || ""}`) {
        case "schedule_title":
        case "intro_content":
        case "intro_note":
            return true
        case "task_content":
        case "task_note":
        case "task_title":
        case "task_frequency":
        case "remove_task":
            return Boolean(findAuthoringRecommendationTask(draft, recommendation)?.id)
        default:
            return false
    }
}

export function clearFocusedAuthoringRecommendationTarget() {
    clearRecommendationRowFlash?.()
    clearSelectedRecommendationField?.()
}

export function focusAuthoringRecommendationTarget({ recommendation, draft }) {
    const target = resolveRecommendationTarget({ recommendation, draft })
    if (!target?.scrollElement) return false

    clearFocusedAuthoringRecommendationTarget()

    target.scrollElement.scrollIntoView({ behavior: "smooth", block: "center" })

    if (target.flashElement && target.flashElement === target.selectedElement) {
        clearSelectedRecommendationField = flashElement(target.flashElement, buildSelectedHighlightStyles())
        clearRecommendationRowFlash = clearSelectedRecommendationField
        return true
    }

    if (target.selectedElement) {
        clearSelectedRecommendationField = highlightSelectedElement(target.selectedElement)
    }

    if (target.flashElement) {
        clearRecommendationRowFlash = flashElement(target.flashElement)
    }

    return true
}

function resolveRecommendationTarget({ recommendation, draft }) {
    switch (`${recommendation?.changeType || ""}`) {
        case "schedule_title":
            return buildSingleElementTarget(document.querySelector('[data-cy="schedule-title-input"]'))
        case "intro_content":
            return buildSingleElementTarget(
                resolveElementBySelectors(['[data-cy="editor-intro.content"]', '[data-cy="schedule-intro-content"]'])
            )
        case "intro_note":
            return buildSingleElementTarget(
                resolveElementBySelectors(['[data-cy="editor-intro.note"]', '[data-cy="schedule-intro-note"]'])
            )
        case "task_content":
        case "task_note":
        case "task_title":
        case "task_frequency":
        case "remove_task":
            return resolveTaskTarget({ recommendation, draft })
        default:
            return null
    }
}

function buildSingleElementTarget(element) {
    if (!element) return null
    return {
        scrollElement: element,
        flashElement: element,
        selectedElement: element,
    }
}

function resolveTaskTarget({ recommendation, draft }) {
    const rowElement = resolveTaskRowElement({ recommendation, draft })
    if (!rowElement) return null

    const fieldElement = resolveTaskFieldElement({ rowElement, recommendation })

    return {
        scrollElement: fieldElement || rowElement,
        flashElement: rowElement,
        selectedElement: fieldElement,
    }
}

function resolveTaskRowElement({ recommendation, draft }) {
    const task = findAuthoringRecommendationTask(draft, recommendation)
    if (!task?.id) return null
    return document.querySelector(buildTaskRowSelector(task.id))
}

function resolveTaskFieldElement({ rowElement, recommendation }) {
    switch (`${recommendation?.changeType || ""}`) {
        case "task_title":
            return rowElement.querySelector('[data-cy="schedule-task-title"]')
        case "task_content":
            return rowElement.querySelector('[data-cy="editor-content"]')
        case "task_note":
            return rowElement.querySelector('[data-cy="editor-note"]')
        case "task_frequency":
            return (
                rowElement.querySelector('[data-cy="task-frequency"]') ||
                rowElement.querySelector('[data-cy="frequency-input"]')
            )
        default:
            return null
    }
}

function resolveElementBySelectors(selectors = []) {
    for (const selector of selectors) {
        const element = document.querySelector(selector)
        if (element) return element
    }
    return null
}

function buildTaskRowSelector(taskId) {
    if (!taskId) return ""
    const escaped = typeof CSS !== "undefined" && CSS.escape ? CSS.escape(taskId) : taskId
    return `[data-cy="task-row-${escaped}"]`
}

function flashElement(element, settleStyles = null) {
    if (!element) return null

    const originalStyles = captureStyles(element)
    const flashStyles = buildFlashHighlightStyles()
    const flashTimer = setTimeout(settleFlashHighlight, 2000)

    applyStyles(element, flashStyles)

    return clearFlashHighlight

    function settleFlashHighlight() {
        if (settleStyles) {
            applyStyles(element, settleStyles)
            return
        }
        restoreStyles(element, originalStyles)
    }

    function clearFlashHighlight() {
        clearTimeout(flashTimer)
        restoreStyles(element, originalStyles)
        if (clearRecommendationRowFlash === clearFlashHighlight) {
            clearRecommendationRowFlash = null
        }
        if (clearSelectedRecommendationField === clearFlashHighlight) {
            clearSelectedRecommendationField = null
        }
    }
}

function highlightSelectedElement(element) {
    if (!element) return null

    const originalStyles = captureStyles(element)

    applyStyles(element, buildSelectedHighlightStyles())

    return clearSelectedHighlight

    function clearSelectedHighlight() {
        restoreStyles(element, originalStyles)
        if (clearSelectedRecommendationField === clearSelectedHighlight) {
            clearSelectedRecommendationField = null
        }
    }
}

function buildFlashHighlightStyles() {
    return {
        transition: "box-shadow 0.3s ease, background-color 0.3s ease",
        boxShadow: "inset 0 0 0 2px rgba(24, 144, 255, 0.4)",
        backgroundColor: "rgba(24, 144, 255, 0.06)",
    }
}

function buildSelectedHighlightStyles() {
    return {
        transition: "box-shadow 0.3s ease, background-color 0.3s ease, outline-color 0.3s ease",
        boxShadow: "0 0 0 2px rgba(24, 144, 255, 0.22)",
        backgroundColor: "rgba(24, 144, 255, 0.09)",
        outline: "1px solid rgba(24, 144, 255, 0.28)",
        outlineOffset: "1px",
        borderRadius: "8px",
    }
}

function captureStyles(element) {
    return {
        transition: element.style.transition,
        boxShadow: element.style.boxShadow,
        backgroundColor: element.style.backgroundColor,
        outline: element.style.outline,
        outlineOffset: element.style.outlineOffset,
        borderRadius: element.style.borderRadius,
    }
}

function applyStyles(element, styles) {
    if (!element || !styles) return
    element.style.transition = styles.transition || ""
    element.style.boxShadow = styles.boxShadow || ""
    element.style.backgroundColor = styles.backgroundColor || ""
    element.style.outline = styles.outline || ""
    element.style.outlineOffset = styles.outlineOffset || ""
    element.style.borderRadius = styles.borderRadius || ""
}

function restoreStyles(element, styles) {
    if (!element || !styles) return
    element.style.transition = styles.transition || ""
    element.style.boxShadow = styles.boxShadow || ""
    element.style.backgroundColor = styles.backgroundColor || ""
    element.style.outline = styles.outline || ""
    element.style.outlineOffset = styles.outlineOffset || ""
    element.style.borderRadius = styles.borderRadius || ""
}
